import {
    normalizeKeywordList,
    normalizeKeywordMap,
    normalizeScrapingConfigurations,
} from "app/pages/digital-marketing/keywords-campaigns/shared/utils/keywordNormalization";

import type {
    IKeywordsCampaignsScrapingKeywords,
    IKeywordsCampaignsUpdateCampaignData,
    IRTBaseCampaignWithSC,
} from "./KeywordsCampaignTypes";

type KeywordChangePayload = {
    scrapingGuid: string;
    addedKeywords?: string[];
    removedKeywords?: string[];
};

type KeywordUpdatePayload = {
    keywordChanges?: KeywordChangePayload[];
    updatedTags?: IKeywordsCampaignsScrapingKeywords;
    updatedKeywordToTargetUrls?: Record<string, string[]>;
};

const normalizeKeywordChanges = <TKeywordChange extends KeywordChangePayload>(
    keywordChanges: TKeywordChange[] | undefined,
): TKeywordChange[] | undefined => {
    return keywordChanges?.map((keywordChange) => ({
        ...keywordChange,
        ...(keywordChange.addedKeywords
            ? { addedKeywords: normalizeKeywordList(keywordChange.addedKeywords) }
            : undefined),
        ...(keywordChange.removedKeywords
            ? { removedKeywords: normalizeKeywordList(keywordChange.removedKeywords) }
            : undefined),
    }));
};

/**
 * Normalizes payload: trims and dedupes keyword strings in `keywordChanges` and
 * aligns `updatedTags` / `updatedKeywordToTargetUrls` keys with those trimmed values.
 */
export const normalizeKeywordUpdatePayload = <TPayload extends KeywordUpdatePayload>(payload: TPayload): TPayload => {
    return {
        ...payload,
        ...(payload.keywordChanges ? { keywordChanges: normalizeKeywordChanges(payload.keywordChanges) } : undefined),
        ...(payload.updatedTags ? { updatedTags: normalizeKeywordMap(payload.updatedTags) } : undefined),
        ...(payload.updatedKeywordToTargetUrls
            ? { updatedKeywordToTargetUrls: normalizeKeywordMap(payload.updatedKeywordToTargetUrls) }
            : undefined),
    };
};

/**
 * Normalizes payload: per-config keyword lists and
 * `tags` / `keywordToTargetUrls` maps use trimmed keyword strings consistently.
 */
export const normalizeCampaignPayload = <TPayload extends IRTBaseCampaignWithSC>(payload: TPayload): TPayload => {
    return {
        ...payload,
        scrapingConfigurations: normalizeScrapingConfigurations(payload.scrapingConfigurations),
        tags: normalizeKeywordMap(payload.tags),
        keywordToTargetUrls: normalizeKeywordMap(payload.keywordToTargetUrls),
    };
};

/**
 * Normalizes payload: applies keyword normalization and trims keyword
 * lists inside `addedScrapingConfigurations` when present.
 */
export const normalizeCampaignSettingsPayload = <TPayload extends IKeywordsCampaignsUpdateCampaignData>(
    payload: TPayload,
): TPayload => {
    const normalizedPayload = normalizeKeywordUpdatePayload(payload);

    return {
        ...normalizedPayload,
        ...(payload.addedScrapingConfigurations
            ? {
                  addedScrapingConfigurations: normalizeScrapingConfigurations(payload.addedScrapingConfigurations),
              }
            : undefined),
    };
};
